import React, {useReducer, useMemo} from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {openSelectedItemForNewStep} from 'analysis-tool/data-frame-creation/DataFrameCreationUtils';
import {DraftableStep} from 'analysis-tool/hooks/useDraftSteps';
import useDraftStepsWithPreview from 'analysis-tool/hooks/useDraftStepsWithPreview';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeInitialConfigState from 'pipelines/generalized-step-config-editor/initial-values/makeInitialConfigState';
import {AddOrReplaceAndRunStepParams, PreviewOutput} from 'pipelines/network/PipelineDAO';
import {PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getLatestStepVersion} from 'pipelines/StepConnectionUtils';
import {AsyncStatus, getMostRecentData} from 'ui-library/hooks/useAsyncState';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';
import ColumnMappingPhase from './ColumnMappingPhase';
import ConfigurePhase from './ConfigurePhase';
import {
  buildDraftInputStepDatas,
  buildStepInputData,
  getPreviewDataFrameColumns,
  stepTypeHasRequiredInputs,
} from './ReplaceSourceUtils';
import SelectStepTypePhase from './SelectStepTypePhase';

type Phase = 'selectStepType' | 'configure' | 'columnMapping';

const DRAFT_STEP_ID = 'replace-source-draft';

interface ReplaceSourceState {
  phase: Phase;
  selectedStepType: PipelineStepType | null;
  leftTable: PipelineViewGraphQL | null;
  rightTable: PipelineViewGraphQL | null;
}

type ReplaceSourceAction =
  | {type: 'selectStepType'; stepType: PipelineStepType | null}
  | {type: 'setTables'; leftTable: PipelineViewGraphQL | null; rightTable: PipelineViewGraphQL | null}
  | {type: 'navigateToConfigure'}
  | {type: 'navigateToSelectStepType'}
  | {type: 'navigateToColumnMapping'}
  | {type: 'reset'};

const INITIAL_STATE: ReplaceSourceState = {
  phase: 'selectStepType',
  selectedStepType: null,
  leftTable: null,
  rightTable: null,
};

function replaceSourceReducer(state: ReplaceSourceState, action: ReplaceSourceAction): ReplaceSourceState {
  switch (action.type) {
    case 'selectStepType':
      return {...state, selectedStepType: action.stepType, leftTable: null, rightTable: null};
    case 'setTables':
      return {...state, leftTable: action.leftTable, rightTable: action.rightTable};
    case 'navigateToConfigure':
      return {...state, phase: 'configure'};
    case 'navigateToSelectStepType':
      return {...state, phase: 'selectStepType'};
    case 'navigateToColumnMapping':
      return {...state, phase: 'columnMapping'};
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Draft config + preview hook
// ---------------------------------------------------------------------------

interface UseReplaceSourceDraftConfigResult {
  draftConfig: Record<string, JSONValue>;
  setDraftConfig: (config: Record<string, JSONValue>) => void;
  previewOutput: PreviewOutput | null;
  isPreviewLoading: boolean;
  previewError: string | null;
  /** Flush the current draftConfig into the draft step (triggers preview generation). */
  commitDraftConfig: () => void;
  clearDraft: () => void;
}

function useReplaceSourceDraftConfig(
  selectedStepType: PipelineStepType | null,
  stepVersion: number,
  inputStepDatas: DraftableStep['inputStepDatas']
): UseReplaceSourceDraftConfigResult {
  const draftableSteps = useMemo(
    (): Array<DraftableStep> =>
      selectedStepType
        ? [{id: DRAFT_STEP_ID, stepType: selectedStepType, stepVersion, config: null, inputStepDatas}]
        : [],
    [selectedStepType, stepVersion, inputStepDatas]
  );

  const {draftSteps, stepPreviews, updateDraftStep, clearDraft} = useDraftStepsWithPreview(draftableSteps);

  const draftStepConfig = draftSteps[DRAFT_STEP_ID]?.config;

  // Without initial defaults, config starts as {} and fields like dataFrameName are missing,
  // causing isValueValid to return false. For UNION_DATASETS, includeDuplicates defaults to
  // true to match the previous CombineTablesSourceRightSidebar behavior.
  const draftConfig = useMemo(() => {
    if (draftStepConfig != null) {
      return draftStepConfig as Record<string, JSONValue>;
    }
    if (!selectedStepType) {
      return {} as Record<string, JSONValue>;
    }
    const initial = makeInitialConfigState(selectedStepType, stepVersion);
    if (selectedStepType === PipelineStepType.UNION_DATASETS) {
      initial.includeDuplicates = true;
    }
    return initial;
  }, [draftStepConfig, selectedStepType, stepVersion]);

  function setDraftConfig(newConfig: Record<string, JSONValue>): void {
    updateDraftStep(DRAFT_STEP_ID, {config: newConfig});
  }

  const previewState = stepPreviews[DRAFT_STEP_ID];
  const previewOutput = previewState ? getMostRecentData(previewState) : null;
  const isPreviewLoading =
    previewState == null ||
    previewState.status === AsyncStatus.LOADING ||
    previewState.status === AsyncStatus.NOT_FETCHED;
  const previewError = previewState?.status === AsyncStatus.ERROR ? previewState.message : null;

  return {
    draftConfig,
    setDraftConfig,
    previewOutput,
    isPreviewLoading,
    previewError,
    commitDraftConfig: () => {
      updateDraftStep(DRAFT_STEP_ID, {config: draftConfig});
    },
    clearDraft: () => {
      clearDraft(DRAFT_STEP_ID);
    },
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ReplaceSourceStepRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
  clientStateId,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.REPLACE_SOURCE>): JSX.Element {
  const [state, dispatch] = useReducer(replaceSourceReducer, INITIAL_STATE);
  const {phase, selectedStepType, leftTable, rightTable} = state;

  const templateDataFrameColumns = getTemplateDataFrameColumns(
    analysis,
    selectedRightSidebarItem.stepIdToReplace
  );
  const stepVersion = selectedStepType ? getLatestStepVersion(selectedStepType) : 0;

  const draftInputStepDatas = useMemo(
    () =>
      selectedStepType && stepTypeHasRequiredInputs(selectedStepType)
        ? buildDraftInputStepDatas(selectedStepType, leftTable, rightTable, analysis.steps)
        : [],
    [selectedStepType, leftTable, rightTable, analysis.steps]
  );

  const {
    draftConfig,
    setDraftConfig,
    previewOutput,
    isPreviewLoading,
    previewError,
    commitDraftConfig,
    clearDraft,
  } = useReplaceSourceDraftConfig(selectedStepType, stepVersion, draftInputStepDatas);

  function handleCancel(): void {
    clearDraft();
    dispatch({type: 'reset'});
    onChangeSelectedRightSidebarItem(null);
  }

  function handleNext(): void {
    if (phase === 'selectStepType') {
      if (!selectedStepType) {
        return;
      }
      clearDraft();
      dispatch({type: 'navigateToConfigure'});
    } else if (phase === 'configure') {
      dispatch({type: 'navigateToColumnMapping'});
      commitDraftConfig();
    }
  }

  function handleBack(): void {
    if (phase === 'configure') {
      dispatch({type: 'navigateToSelectStepType'});
    } else if (phase === 'columnMapping') {
      dispatch({type: 'navigateToConfigure'});
    }
  }

  async function handleSubmitReplace(selectedColumns: Array<DataFrameColumnDef | null>): Promise<void> {
    if (!selectedStepType) {
      return;
    }
    // Cast is necessary because TypeScript can't narrow a dynamic PipelineStepType to a specific literal.
    const stepParams = {
      stepType: selectedStepType,
      stepVersion,
      stepConfig: draftConfig,
      ...(stepTypeHasRequiredInputs(selectedStepType) &&
        leftTable &&
        rightTable && {
          ...buildStepInputData(selectedStepType, leftTable, rightTable, analysis.steps),
        }),
    } as AddOrReplaceAndRunStepParams;

    const newDataFrameColumns = getPreviewDataFrameColumns(previewOutput, selectedStepType);

    const columnMapping =
      selectedColumns.length > 0
        ? selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns)
        : undefined;
    // allDisplayNames is only needed for step types that use index-based column mapping
    // (UPLOAD_CSV_DATASET and CONNECTION_DATA_DATASET). For other step types, the column mapping
    // values already contain system names, not indices. Sending allDisplayNames for other types
    // causes a backend validation error.
    const stepTypesWithIndexInColumnMapping: ReadonlyArray<PipelineStepType> = [
      PipelineStepType.UPLOAD_CSV_DATASET,
      PipelineStepType.CONNECTION_DATA_DATASET,
    ];
    const allDisplayNames =
      newDataFrameColumns != null && stepTypesWithIndexInColumnMapping.includes(selectedStepType)
        ? newDataFrameColumns.map((column) => column.displayName)
        : undefined;

    const newStep = await withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, stepParams, {
        originalStepId: selectedRightSidebarItem.stepIdToReplace,
        columnMapping,
        allDisplayNames,
      })
    );
    clearDraft();
    dispatch({type: 'reset'});
    onChangeSelectedRightSidebarItem(null);
    openSelectedItemForNewStep(newStep, onChangeSelectedItem);
  }

  if (phase === 'selectStepType') {
    return (
      <SelectStepTypePhase
        selectedStepType={selectedStepType}
        setSelectedStepType={(stepType) => {
          dispatch({type: 'selectStepType', stepType});
        }}
        onCancel={handleCancel}
        onNext={handleNext}
      />
    );
  }

  if (phase === 'configure' && selectedStepType) {
    return (
      <ConfigurePhase
        state={{selectedStepType, leftTable, rightTable}}
        onSetTables={(left, right) => {
          dispatch({type: 'setTables', leftTable: left, rightTable: right});
        }}
        stepVersion={stepVersion}
        draftConfig={draftConfig}
        setDraftConfig={setDraftConfig}
        clientStateId={clientStateId}
        onBack={handleBack}
        onNext={handleNext}
        analysis={analysis}
      />
    );
  }

  if (phase === 'columnMapping' && selectedStepType) {
    return (
      <ColumnMappingPhase
        selectedStepType={selectedStepType}
        templateDataFrameColumns={templateDataFrameColumns}
        previewOutput={previewOutput}
        isPreviewLoading={isPreviewLoading}
        previewError={previewError}
        onBack={handleBack}
        onSubmit={handleSubmitReplace}
      />
    );
  }

  throw new Error(`Unexpected replace source sidebar state: phase=${phase}, stepType=${selectedStepType}`);
}
